import {
    AddressComponent,
    GooglePlaceDetail,
    PlaceType,
} from "react-native-google-places-autocomplete";

export type AddressCountry = {
    name: string;
    code: string;
};

export type AddressState = {
    name: string;
    code: string;
    country: AddressCountry;
};

export type AddressLocality = {
    name: string;
    postalCode?: string | null;
    state: AddressState;
};

export type Address = {
    formatted: string;
    streetNumber: string;
    route: string;
    locality: AddressLocality;
    latitude?: number | null;
    longitude?: number | null;
};

export type GoogleAddressComponent = AddressComponent;

const _extractComponent = (
    components: GoogleAddressComponent[],
    type: PlaceType,
): GoogleAddressComponent | null => {
    for (const component of components) {
        if (component.types.indexOf(type) !== -1) {
            return component;
        }
    }
    return null;
};

const _extractCountry = (components: GoogleAddressComponent[]): AddressCountry | null => {
    const component = _extractComponent(components, "country");
    if (component) {
        return { name: component.long_name, code: component.short_name };
    }
    return null;
};

const _extractState = (
    components: GoogleAddressComponent[],
    country: AddressCountry,
): AddressState | null => {
    const component = _extractComponent(components, "administrative_area_level_1");
    if (component) {
        return { name: component.long_name, code: component.short_name, country: country };
    }
    return null;
};

const _extractLocality = (
    components: GoogleAddressComponent[],
    state: AddressState,
): AddressLocality | null => {
    const localityComponent =
        _extractComponent(components, "postal_town") ||
        _extractComponent(components, "locality") ||
        _extractComponent(components, "sublocality");
    const postalComponent = _extractComponent(components, "postal_code") || {
        long_name: "",
    };
    if (localityComponent && postalComponent) {
        return { name: localityComponent.long_name, postalCode: postalComponent.long_name, state };
    }
    return null;
};

const _extractStreetNumber = (components: GoogleAddressComponent[]): string => {
    const streetNumberComponent = _extractComponent(components, "street_number");
    return streetNumberComponent?.long_name || "";
};

const _extractRoute = (components: GoogleAddressComponent[]): string => {
    const routeComponent = _extractComponent(components, "route");
    return routeComponent?.long_name || "";
};

const _extractAddressComponents = (
    components: GoogleAddressComponent[],
): Partial<Address> | null => {
    const country = _extractCountry(components);
    if (!country) {
        return null;
    }
    const state = _extractState(components, country);
    if (!state) {
        return null;
    }
    const locality = _extractLocality(components, state);
    if (!locality) {
        return null;
    }
    const streetNumber = _extractStreetNumber(components);
    const route = _extractRoute(components);

    return {
        streetNumber,
        route,
        locality,
    };
};

export const extractAddress = (details: GooglePlaceDetail): Address | null => {
    const partialAddress = _extractAddressComponents(details.address_components);
    if (partialAddress?.locality) {
        return {
            ...partialAddress,
            formatted: details.formatted_address,
            longitude: details.geometry.location.lng,
            latitude: details.geometry.location.lat,
        } as Address;
    }
    return null;
};
